import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { IQueryParam } from 'modelos/src';
import { Subscription, from, switchMap, timer } from 'rxjs';
import { EmergenciasService } from '../../../../../auxiliares/servicios/http/emergencias.service';
import {
  ListadoEmergenciasTablaComponent,
  SolapaEmergencias,
  filtroBaseEmergencias,
} from './listado-emergencias-tabla/listado-emergencias-tabla.component';

interface ISolapa {
  id: SolapaEmergencias;
  label: string;
  icono: string;
  contador?: number;
}

/**
 * Listado de emergencias médicas en tres solapas: Auxilios y En seguimiento
 * (activas) y Historial (finalizadas, canceladas e importadas). La solapa
 * activa queda en `?tab=` para poder compartir el enlace.
 */
@Component({
  selector: 'app-listados-emergencias-medicas',
  templateUrl: './listados-emergencias-medicas.component.html',
  styleUrl: './listados-emergencias-medicas.component.scss',
  imports: [MatTabsModule, MatIconModule, ListadoEmergenciasTablaComponent],
})
export class ListadosEmergenciasMedicasComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private emergenciasService = inject(EmergenciasService);

  public solapas: ISolapa[] = [
    { id: 'auxilios', label: 'Auxilios', icono: 'emergency' },
    { id: 'seguimiento', label: 'En seguimiento', icono: 'monitor_heart' },
    { id: 'historico', label: 'Historial', icono: 'history' },
  ];
  public selectedTab = 0;

  private contadores$?: Subscription;
  private params$?: Subscription;

  public get solapaActual(): SolapaEmergencias {
    return this.solapas[this.selectedTab]?.id ?? 'auxilios';
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.solapaActual },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Cantidad de emergencias activas por solapa (refresca cada 30 s). */
  private contarActivas(): void {
    const activas = this.solapas.filter((s) => s.id !== 'historico');
    this.contadores$ = timer(0, 30_000)
      .pipe(
        switchMap(() =>
          from(
            Promise.all(
              activas.map((s): Promise<number | undefined> => {
                const query: IQueryParam = {
                  filter: JSON.stringify(filtroBaseEmergencias(s.id)),
                  select: '_id',
                  limit: 1,
                  includeChildren: true,
                };
                return this.emergenciasService
                  .getFiltered(query)
                  .then((r) => r.totalCount)
                  .catch((): undefined => undefined);
              }),
            ),
          ),
        ),
      )
      .subscribe((totales) => {
        activas.forEach((s, i) => (s.contador = totales[i]));
      });
  }

  public refrescarContadores(): void {
    this.contadores$?.unsubscribe();
    this.contarActivas();
  }

  ngOnInit(): void {
    this.params$ = this.route.queryParamMap.subscribe((params) => {
      const tab = params.get('tab');
      if (!tab) return;
      const porNombre = this.solapas.findIndex((s) => s.id === tab);
      const indice = porNombre >= 0 ? porNombre : Number(tab);
      if (Number.isInteger(indice) && indice >= 0 && indice < 3) {
        this.selectedTab = indice;
      }
    });
    this.contarActivas();
  }

  ngOnDestroy(): void {
    this.contadores$?.unsubscribe();
    this.params$?.unsubscribe();
  }
}
